import { Button } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { CityQuestRO } from '@routellers/types/dist/city-quest/city-quest.ro';
import { CityQuestStatusEnum } from '@routellers/types/dist/city-quest/enum/city-quest-status.enum';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { api, apiErrorMessage } from '../api';
import { EntityEditorShell } from '../components/editor';
import { enumLabel } from '../i18n';

import { QuestConstructorTab } from './quest/quest-constructor-tab';
import { QuestLocalesTab } from './quest/quest-locales-tab';
import { QuestOverviewTab } from './quest/quest-overview-tab';

type Props = {
    opened: boolean;
    onClose: () => void;
    questId: number | null;
    onSaved: () => void;
    onDeleted?: () => void;
    initialTab?: string;
};

const statusColors: Record<CityQuestStatusEnum, string> = {
    [CityQuestStatusEnum.DRAFT]: 'gray',
    [CityQuestStatusEnum.NEED_REVIEW]: 'orange',
    [CityQuestStatusEnum.REJECTED]: 'red',
    [CityQuestStatusEnum.READY]: 'green',
    [CityQuestStatusEnum.PAUSED]: 'blue',
};

const titleOf = (quest: CityQuestRO | null, t: (key: string, options?: any) => string) => {
    if (!quest) return t('editors.quest.fallbackTitle');

    return quest.title || quest.key || t('editors.quest.fallbackTitleId', { id: quest.id });
};

const normalizeTab = (value: string) => {
    if (value === 'overview') return 'meta';
    if (value === 'content') return 'constructor';
    if (value === 'assets') return 'meta';

    return value;
};

export const QuestEditor = ({
    opened,
    onClose,
    questId,
    onSaved,
    onDeleted,
    initialTab = 'meta',
}: Props) => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [quest, setQuest] = useState<CityQuestRO | null>(null);
    const [tab, setTab] = useState(normalizeTab(initialTab));
    const [tabDirty, setTabDirty] = useState(false);

    useEffect(() => {
        if (!opened || questId == null) return;

        let cancelled = false;

        const load = async () => {
            setLoading(true);
            setQuest(null);
            setTab(normalizeTab(initialTab));
            setTabDirty(false);

            try {
                const { data } = await api.cityQuestsApi.getOne(questId);

                if (!cancelled && data) setQuest(data);
            } catch (error) {
                notifications.show({ color: 'red', message: apiErrorMessage(error) });
                onClose();
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        load();

        return () => {
            cancelled = true;
        };
    }, [opened, questId, initialTab, onClose]);

    const onDirtyChange = useCallback((dirty: boolean) => setTabDirty(dirty), []);

    const handleTabChange = (next: string) => {
        if (tabDirty) {
            const ok = window.confirm(t('common.confirmSwitchTab'));

            if (!ok) return;
        }

        setTabDirty(false);
        setTab(next);
    };

    const tabs = useMemo(
        () => [
            {
                value: 'meta',
                label: t('editors.quest.tabs.meta'),
                dirty: tab === 'meta' && tabDirty,
            },
            {
                value: 'constructor',
                label: t('editors.quest.tabs.constructor'),
                dirty: tab === 'constructor' && tabDirty,
            },
            {
                value: 'locales',
                label: t('editors.quest.tabs.locales'),
                dirty: tab === 'locales' && tabDirty,
            },
        ],
        [tab, tabDirty, t],
    );

    return (
        <EntityEditorShell
            opened={opened}
            onClose={onClose}
            loading={loading || !quest}
            title={titleOf(quest, t)}
            subtitle={
                quest ? `${quest.key}${quest.cityTitle ? ` · ${quest.cityTitle}` : ''}` : undefined
            }
            status={
                quest
                    ? {
                          label: enumLabel(t, 'enums.cityQuestStatus', quest.status),
                          color: statusColors[quest.status] || 'gray',
                      }
                    : undefined
            }
            dirty={tabDirty}
            tabs={tabs}
            activeTab={tab}
            onTabChange={handleTabChange}
            size='95%'
            headerRight={
                quest ? (
                    <Button
                        size='xs'
                        color='red'
                        variant='light'
                        loading={deleting}
                        onClick={async () => {
                            if (questId == null) return;
                            if (
                                !window.confirm(
                                    t('editors.quest.deleteConfirm', { key: quest.key }),
                                )
                            ) {
                                return;
                            }

                            setDeleting(true);

                            try {
                                await api.cityQuestsApi.remove(questId);
                                notifications.show({
                                    color: 'green',
                                    message: t('editors.quest.deleted'),
                                });
                                onDeleted?.();
                                onSaved();
                                onClose();
                            } catch (error) {
                                notifications.show({
                                    color: 'red',
                                    message: apiErrorMessage(error),
                                });
                            } finally {
                                setDeleting(false);
                            }
                        }}
                    >
                        {t('editors.quest.delete')}
                    </Button>
                ) : undefined
            }
        >
            {quest && tab === 'meta' && (
                <QuestOverviewTab
                    quest={quest}
                    onUpdated={(q) => {
                        setQuest(q);
                        onSaved();
                    }}
                    onClose={onClose}
                    onDirtyChange={onDirtyChange}
                />
            )}
            {quest && tab === 'constructor' && (
                <QuestConstructorTab
                    questId={quest.id}
                    questCityId={quest.cityId}
                    questCountry={quest.country}
                    localePrimary={quest.localePrimary}
                    onClose={onClose}
                    onDirtyChange={onDirtyChange}
                    onSaved={onSaved}
                />
            )}
            {quest && tab === 'locales' && (
                <QuestLocalesTab
                    quest={quest}
                    onUpdated={(q) => {
                        setQuest(q);
                        onSaved();
                    }}
                    onClose={onClose}
                    onDirtyChange={onDirtyChange}
                />
            )}
        </EntityEditorShell>
    );
};
